/**
 * Mobile primary navigation toggle.
 *
 * The CSS in `_nav.scss` hides `.site-nav__inner` at tablet width and
 * reveals it again when the `.is-open` modifier is present. This module
 * wires up the hamburger button (`.site-nav__toggle`) to flip that
 * class, mirror `aria-expanded` on the trigger, and close the menu on
 * outside clicks or the Escape key.
 *
 * Pure DOM — no jQuery — same pattern as `_faq.js`. Delegated handler
 * is harmless on desktop because the toggle is `display: none` and
 * never receives clicks.
 */

const TOGGLE_SELECTOR = '.site-nav__toggle';
const SUBMENU_TOGGLE_SELECTOR = '.site-nav__submenu-toggle';
const SUBMENU_PARENT_SELECTOR = '.site-nav .menu-item-has-children, .site-nav .menu-item-has-mega-menu';
const NAV_SELECTOR = '.site-nav';
const OPEN_CLASS = 'is-open';

/**
 * Inject a `<button class="site-nav__submenu-toggle">` between the
 * link and its dropdown for every top-level item that has a sub-menu
 * or mega menu. The button is `display: none` on desktop (the
 * existing `::after` caret on the link handles indication there) and
 * becomes the tap target on mobile, where tapping it toggles the
 * `.is-open` class on the <li> to reveal the dropdown.
 *
 * The visual caret is rendered via the button's CSS `::before`, so we
 * don't need any inner content.
 */
const initSubmenuToggles = () => {
    document.querySelectorAll(SUBMENU_PARENT_SELECTOR).forEach((li) => {
        if (li.querySelector(`:scope > ${SUBMENU_TOGGLE_SELECTOR}`)) return;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'site-nav__submenu-toggle';
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-label', 'Toggle submenu');

        // Inject right after the link so the link grows on the left and
        // the toggle sits on the right; the submenu/mega-menu (next
        // sibling) wraps onto its own row via flex-basis: 100%.
        const link = li.querySelector(':scope > a');
        if (link) {
            link.insertAdjacentElement('afterend', button);
        } else {
            li.appendChild(button);
        }
    });
};

initSubmenuToggles();

const closeNav = (toggle, target) => {
    target.classList.remove(OPEN_CLASS);
    toggle.setAttribute('aria-expanded', 'false');
};

document.addEventListener('click', (event) => {
    // Per-item submenu / mega-menu toggle (mobile). Stop the click from
    // bubbling to the outside-click handler below, which would close
    // the whole nav.
    const submenuToggle = event.target.closest(SUBMENU_TOGGLE_SELECTOR);
    if (submenuToggle) {
        event.preventDefault();
        event.stopPropagation();
        const li = submenuToggle.closest('li');
        if (!li) return;
        const willOpen = !li.classList.contains(OPEN_CLASS);
        li.classList.toggle(OPEN_CLASS, willOpen);
        submenuToggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        return;
    }

    const toggle = event.target.closest(TOGGLE_SELECTOR);
    if (toggle) {
        const targetId = toggle.getAttribute('aria-controls');
        const target = targetId ? document.getElementById(targetId) : null;
        if (!target) return;

        const willOpen = !target.classList.contains(OPEN_CLASS);
        target.classList.toggle(OPEN_CLASS, willOpen);
        toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        return;
    }

    // Click outside the nav while it's open → close. We check every
    // open nav on the page so a future second instance wouldn't break.
    document.querySelectorAll(NAV_SELECTOR).forEach((nav) => {
        if (nav.contains(event.target)) return;

        const navToggle = nav.querySelector(TOGGLE_SELECTOR);
        const navTargetId = navToggle && navToggle.getAttribute('aria-controls');
        const navTarget = navTargetId ? document.getElementById(navTargetId) : null;
        if (!navToggle || !navTarget) return;

        if (navTarget.classList.contains(OPEN_CLASS)) {
            closeNav(navToggle, navTarget);
        }
    });
});

document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    document.querySelectorAll(NAV_SELECTOR).forEach((nav) => {
        const navToggle = nav.querySelector(TOGGLE_SELECTOR);
        const navTargetId = navToggle && navToggle.getAttribute('aria-controls');
        const navTarget = navTargetId ? document.getElementById(navTargetId) : null;
        if (!navToggle || !navTarget) return;

        if (navTarget.classList.contains(OPEN_CLASS)) {
            closeNav(navToggle, navTarget);
            navToggle.focus();
        }
    });
});
